import { isDAProvider } from '@lyrahealth-inc/shared-app-logic/src/features/providers/utils'
import {
  ProviderPairConversationObject,
  ProviderPairProgram,
} from '@lyrahealth-inc/shared-app-logic/src/models/conversation/CoordinatedCareConversation'
import { CONVERSATION_STATE, CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import type { CareHistoryProviderStatus } from '@lyrahealth-inc/shared-app-logic/src/models/providers/CareHistoryProviders'

import { getProviderPairCounterpartStatus } from './providerPairPresentation'
import {
  MessengerConversation,
  MessengerParticipant,
  MessengerThread,
  PARTICIPANT_CONFIG,
  ProviderPairAccess,
} from './types'

import type { EpisodeParticipantRoster } from '../../episodes/data/episodesApi'

export { ProviderPairAccess } from './types'

export type ProviderPairRelationshipStatus = 'current' | 'historical'

export type ProviderPairProviderProfile = {
  providerLyraId: string
  displayName: string
  role?: string
  relationshipStatus?: ProviderPairRelationshipStatus
  status?: CareHistoryProviderStatus
  programs?: ProviderPairProgram[]
  employmentStatus?: string
  network?: string
}

export type ProviderPairThread = {
  id: string
  conversationId: string
  databaseConversationId: string
  clientId: string
  currentProviderId: string
  counterpart: ProviderPairProviderProfile
  unreadCount: number
  access: ProviderPairAccess
  recipientIsDirectAccess: boolean
}

/** POST /conversations/participants returns identity; read state comes from GET. */
export type ProviderPairCreateResponse = {
  id: string
  external_conversation_id: string
  type: CONVERSATION_TYPE.PROVIDER_PROVIDER
  status: CONVERSATION_STATE
  created: boolean
  reinstated: boolean
  episode_ids: string[]
}

export type ProviderPairProfileResolver = (providerLyraId: string) => ProviderPairProviderProfile | undefined

type ProviderPairParticipants = {
  currentProviderId: string
  counterpartProviderId: string
}

const MALFORMED_PROVIDER_PAIR_RESPONSE_WARNING = 'Discarding malformed provider-pair conversation response'

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && Boolean(value.trim())

const isProviderPairParticipant = (value: unknown): value is ProviderPairConversationObject['participants'][number] =>
  isRecord(value) &&
  value.type === 'provider' &&
  isNonEmptyString(value.participant_lyra_id) &&
  (value.access === 'read' || value.access === 'write' || value.access === 'revoked')

const isProviderPairCurrentUser = (value: unknown): value is ProviderPairConversationObject['current_user'] => {
  if (!isRecord(value)) {
    return false
  }

  const { transport_status: transportStatus, unread_count: unreadCount } = value
  return (
    isProviderPairParticipant(value) &&
    (value.access === 'read' || value.access === 'write') &&
    (transportStatus === 'active' || transportStatus === 'inactive' || transportStatus === 'closed') &&
    (unreadCount === undefined ||
      (typeof unreadCount === 'number' && Number.isInteger(unreadCount) && unreadCount >= 0))
  )
}

const isOptionalDateString = (value: unknown): boolean =>
  value === undefined || value === null || (isNonEmptyString(value) && Number.isFinite(Date.parse(value)))

const isProviderPairIdentity = (value: unknown): value is Record<string, unknown> =>
  isRecord(value) &&
  value.type === CONVERSATION_TYPE.PROVIDER_PROVIDER &&
  Object.values(CONVERSATION_STATE).some((state) => state === value.status) &&
  isNonEmptyString(value.id) &&
  isNonEmptyString(value.external_conversation_id) &&
  Array.isArray(value.episode_ids) &&
  value.episode_ids.length > 0 &&
  value.episode_ids.every(isNonEmptyString)

export const isProviderPairConversation = (value: unknown): value is ProviderPairConversationObject => {
  if (!isProviderPairIdentity(value)) {
    return false
  }

  const {
    anchor_patient_lyra_id: anchorPatientId,
    capabilities,
    current_user: currentUser,
    participants,
    patient_lyra_id: patientId,
  } = value
  // The backend sets both fields to the server-resolved anchor for provider pairs.
  if (
    !isNonEmptyString(patientId) ||
    !isNonEmptyString(anchorPatientId) ||
    anchorPatientId !== patientId ||
    !isOptionalDateString(value.conversation_last_message_sent_datetime) ||
    (value.created !== undefined && typeof value.created !== 'boolean') ||
    !Array.isArray(participants) ||
    participants.length !== 2 ||
    !participants.every(isProviderPairParticipant) ||
    !isProviderPairCurrentUser(currentUser) ||
    !isRecord(capabilities) ||
    typeof capabilities.can_read !== 'boolean' ||
    typeof capabilities.can_send !== 'boolean'
  ) {
    return false
  }

  const providerIds = participants.map(({ participant_lyra_id: participantId }) => participantId)
  return new Set(providerIds).size === 2 && providerIds.includes(currentUser.participant_lyra_id)
}

// The backend owns send authorization; participant access and transport status do not override can_send.
export const getProviderPairAccess = ({ capabilities }: ProviderPairConversationObject): ProviderPairAccess =>
  capabilities.can_send ? ProviderPairAccess.WRITE : ProviderPairAccess.READ

const warnMalformedProviderPairConversation = () => console.warn(MALFORMED_PROVIDER_PAIR_RESPONSE_WARNING)

const throwMalformedProviderPairConversation = (): never => {
  warnMalformedProviderPairConversation()
  throw new Error('Malformed provider-pair conversation response')
}

export const parseProviderPairConversationResponse = (value: unknown): ProviderPairConversationObject =>
  isProviderPairConversation(value) ? value : throwMalformedProviderPairConversation()

export const parseProviderPairCreateResponse = (value: unknown): ProviderPairCreateResponse => {
  if (!isProviderPairIdentity(value) || typeof value.created !== 'boolean' || typeof value.reinstated !== 'boolean') {
    console.warn('Discarding malformed provider-pair create response')
    throw new Error('Malformed provider-pair create response')
  }
  return value as ProviderPairCreateResponse
}

export const parseProviderPairConversationListResponse = (value: unknown): ProviderPairConversationObject[] => {
  if (!Array.isArray(value)) {
    return throwMalformedProviderPairConversation()
  }

  return value.filter((conversation): conversation is ProviderPairConversationObject => {
    if (isProviderPairConversation(conversation)) {
      return true
    }
    warnMalformedProviderPairConversation()
    return false
  })
}

const PROVIDER_PAIR_THREAD_ID_PREFIX = 'provider-pair:'

const getProviderPairParticipants = (conversation: ProviderPairConversationObject): ProviderPairParticipants | null => {
  const { current_user: currentUser, participants } = conversation
  if (
    currentUser.type !== 'provider' ||
    !isNonEmptyString(currentUser.participant_lyra_id) ||
    participants.length !== 2 ||
    participants.some(
      ({ participant_lyra_id: participantId, type }) => type !== 'provider' || !isNonEmptyString(participantId),
    )
  ) {
    return null
  }

  const providerIds = participants.map(({ participant_lyra_id: participantId }) => participantId)
  if (new Set(providerIds).size !== 2 || !providerIds.includes(currentUser.participant_lyra_id)) {
    return null
  }

  const counterpart = participants.find(
    ({ participant_lyra_id: participantId }) => participantId !== currentUser.participant_lyra_id,
  )
  return counterpart
    ? {
        currentProviderId: currentUser.participant_lyra_id,
        counterpartProviderId: counterpart.participant_lyra_id,
      }
    : null
}

export const isProviderPairThreadId = (threadId: string | null | undefined): boolean =>
  threadId?.startsWith(PROVIDER_PAIR_THREAD_ID_PREFIX) ?? false

export const getProviderPairProfile = (
  rosters: EpisodeParticipantRoster[],
  clientId: string,
  providerLyraId: string,
): ProviderPairProviderProfile | undefined => {
  const participant = rosters
    .filter(({ anchor_patient_lyra_id: anchorPatientId }) => anchorPatientId === clientId)
    .flatMap(({ participants }) => participants)
    .find(({ lyra_id: participantId, type }) => participantId === providerLyraId && type === 'provider')
  if (!participant) {
    return undefined
  }

  const displayName = [participant.first_name, participant.last_name].filter(isNonEmptyString).join(' ')
  if (!displayName) {
    return undefined
  }

  return {
    providerLyraId,
    displayName,
    role: participant.role,
    // The roster supplies names only; care history owns relationship and provider status.
  }
}

export const getProviderPairThreadId = (
  clientId: string,
  firstProviderId: string,
  secondProviderId: string,
): string => {
  const pair = [firstProviderId, secondProviderId].sort().map(encodeURIComponent).join(':')
  return `${PROVIDER_PAIR_THREAD_ID_PREFIX}${encodeURIComponent(clientId)}:${pair}`
}

export const buildProviderPairThread = (
  conversation: unknown,
  resolveProfile: ProviderPairProfileResolver,
): ProviderPairThread | null => {
  if (!isProviderPairConversation(conversation)) {
    warnMalformedProviderPairConversation()
    return null
  }

  const { capabilities, current_user: currentUser } = conversation
  const providerPairParticipants = getProviderPairParticipants(conversation)
  if (!capabilities.can_read || !providerPairParticipants) {
    return null
  }

  const { currentProviderId, counterpartProviderId } = providerPairParticipants

  const counterpart = resolveProfile(counterpartProviderId)
  if (
    !counterpart ||
    counterpart.providerLyraId !== counterpartProviderId ||
    !isNonEmptyString(counterpart.displayName)
  ) {
    return null
  }

  return {
    id: getProviderPairThreadId(conversation.patient_lyra_id, currentProviderId, counterpartProviderId),
    conversationId: conversation.external_conversation_id,
    databaseConversationId: conversation.id,
    clientId: conversation.patient_lyra_id,
    currentProviderId,
    counterpart,
    unreadCount: currentUser.unread_count ?? 0,
    access: getProviderPairAccess(conversation),
    recipientIsDirectAccess: isDirectAccessCounterpart(counterpart),
  }
}

const isDirectAccessCounterpart = (counterpart: ProviderPairProviderProfile) =>
  isDAProvider({
    programTaxonomy: counterpart.programs?.flatMap(({ partner }) => (partner ? [{ partner }] : [])),
  })

export const getProviderPairThreadFlags = (access: ProviderPairAccess) => {
  const config = PARTICIPANT_CONFIG[MessengerParticipant.PROVIDER_PAIR]
  const isReadOnly = access !== ProviderPairAccess.WRITE
  return {
    canSendMessages: config.canSendMessages && !isReadOnly,
    showInputActions: config.showInputActions && !isReadOnly,
    disableDrafts: config.disableDrafts || isReadOnly,
    providerPairAccess: access,
  }
}

// buildProviderPairThread has already dropped unreadable pairs. A draft has no conversation yet.
export const buildProviderPairMessengerThread = (
  thread: Pick<ProviderPairThread, 'id' | 'counterpart' | 'unreadCount' | 'access' | 'recipientIsDirectAccess'> & {
    conversationId: string | null
  },
): MessengerThread => {
  const config = PARTICIPANT_CONFIG[MessengerParticipant.PROVIDER_PAIR]
  return {
    id: thread.id,
    participant: MessengerParticipant.PROVIDER_PAIR,
    conversationType: CONVERSATION_TYPE.PROVIDER_PROVIDER,
    conversationId: thread.conversationId,
    conversation: null,
    recipientId: thread.counterpart.providerLyraId,
    recipientName: thread.counterpart.displayName,
    tabLabel: thread.counterpart.displayName,
    unreadCount: config.showUnreadBadge ? thread.unreadCount : 0,
    recipientIsDirectAccess: thread.recipientIsDirectAccess,
    ...getProviderPairThreadFlags(thread.access),
    providerPairStatus: getProviderPairCounterpartStatus(thread.counterpart),
  }
}

// A pair opened from Past care before its conversation exists. The first send creates it, so an
// abandoned draft leaves nothing behind. Saved drafts are keyed by conversation id, so they stay off.
export const buildProviderPairDraftMessengerThread = (
  clientId: string,
  currentProviderId: string,
  counterpart: ProviderPairProviderProfile,
): MessengerThread => ({
  ...buildProviderPairMessengerThread({
    id: getProviderPairThreadId(clientId, currentProviderId, counterpart.providerLyraId),
    conversationId: null,
    counterpart,
    unreadCount: 0,
    access: ProviderPairAccess.WRITE,
    recipientIsDirectAccess: isDirectAccessCounterpart(counterpart),
  }),
  disableDrafts: true,
})

export const buildProviderPairMessengerConversation = (conversation: unknown): MessengerConversation | null => {
  if (!isProviderPairConversation(conversation)) {
    return null
  }

  const providerPairParticipants = getProviderPairParticipants(conversation)
  if (!providerPairParticipants) {
    return null
  }

  const { currentProviderId, counterpartProviderId } = providerPairParticipants
  return {
    conversation_attributes: {
      type: CONVERSATION_TYPE.PROVIDER_PROVIDER,
    },
    conversation_id: conversation.external_conversation_id,
    db_conversation_id: conversation.id,
    patient_lyra_id: conversation.patient_lyra_id,
    provider_lyra_id: currentProviderId,
    counterpartProviderId,
    providerPairUnreadCount: conversation.current_user.unread_count ?? 0,
  }
}
